import { Box, Group, Progress, Text, useMantineTheme } from '@mantine/core';
import { IconArrowUpRight } from '@tabler/icons-react';
import { computeDebtorBalance } from 'app/Private/Clients/Detail/BalanceTab/ComputeDebtorBalance';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { Panel } from 'components/Panel';
import { tKeys, useTranslation } from 'locales';
import { useLoadUnhandledClient } from 'modules/AtRisk/api/use-load-unhandled-client';
import { useHistory } from 'react-router';
import { useLoadDebtorBalance } from 'shared/hooks/use-load-debtor-balance';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import { computeOverviewBar } from './overview-bar';

export const UnhandledInvoicesOverview = () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const history = useHistory();
  const { id } = useDebtor();
  const { debtorBalance } = useLoadDebtorBalance(String(id));
  const { unhandledClient } = useLoadUnhandledClient({ debtorId: String(id) });

  const actionableInvoiceIds = new Set(
    unhandledClient.unhandledInvoices.map(invoice => invoice.id),
  );

  const { openInvoices } = computeDebtorBalance(debtorBalance);
  const bar = computeOverviewBar(debtorBalance, actionableInvoiceIds);
  const netOwed = bar.aTraiter + bar.enCours + bar.notDue;

  const formatter = useLocalizedCurrencyFormatter();

  const toPercent = (value: number) => (bar.gross > 0 ? (value / bar.gross) * 100 : 0);

  const goToBalanceDetail = () => history.replace('#balance-detail');

  return (
    <Box role="button" tabIndex={0} onClick={goToBalanceDetail} style={{ cursor: 'pointer' }}>
      <Panel pt={12} pb={18} pl={16}>
        <Group justify="space-between" mb="xs" align="flex-start">
          <Group gap={6} align="baseline">
            <Text fw={700}>{formatter.format(netOwed)}</Text>
            <Text size="xs" c="dimmed">
              {t(tKeys.MODULES.AT_RISK.OVERVIEW.TO_BE_RECEIVED)}
            </Text>
            <Text size="xs" c="dimmed">
              {t(tKeys.MODULES.AT_RISK.OVERVIEW.OUT_OF)}
            </Text>
            <Text fw={700}>{formatter.format(bar.gross)}</Text>
            <Text size="xs" c="dimmed">
              · {t(tKeys.MODULES.AT_RISK.OVERVIEW.INVOICES_COUNT, { count: openInvoices.length })}
            </Text>
          </Group>
          <IconArrowUpRight size={18} color={theme.colors.gray[6]} />
        </Group>

        <Progress.Root size="lg" radius="lg">
          {bar.notDue > 0 && <Progress.Section value={toPercent(bar.notDue)} color="gray.6" />}
          {bar.credited > 0 && <Progress.Section value={toPercent(bar.credited)} color="green.7" />}
          {bar.enCours > 0 && <Progress.Section value={toPercent(bar.enCours)} color="orange.6" />}
          {bar.aTraiter > 0 && <Progress.Section value={toPercent(bar.aTraiter)} color="red.8" />}
        </Progress.Root>

        <Group gap="md" mt="sm">
          {bar.notDue > 0 && (
            <OverviewLegend
              color="gray.6"
              label={`${formatter.format(bar.notDue)} ${t(tKeys.MODULES.AT_RISK.OVERVIEW.NOT_DUE)}`}
            />
          )}
          {bar.credited > 0 && (
            <OverviewLegend
              color="green.7"
              label={`${formatter.format(bar.credited)} ${t(tKeys.MODULES.AT_RISK.OVERVIEW.CREDITED)}`}
            />
          )}
          {bar.enCours > 0 && (
            <OverviewLegend
              color="orange.6"
              label={`${formatter.format(bar.enCours)} ${t(tKeys.MODULES.AT_RISK.OVERVIEW.IN_PROGRESS)}`}
            />
          )}
          {bar.aTraiter > 0 && (
            <OverviewLegend
              color="red.8"
              label={`${formatter.format(bar.aTraiter)} ${t(tKeys.MODULES.AT_RISK.OVERVIEW.TO_HANDLE)}`}
            />
          )}
        </Group>
      </Panel>
    </Box>
  );
};

type OverviewLegendProps = {
  color: string;
  label: string;
};

const OverviewLegend = ({ color, label }: OverviewLegendProps) => {
  return (
    <Group gap={6}>
      <Box w={10} h={10} bg={color} style={{ borderRadius: '50%', flexShrink: 0 }} />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
};
